import * as Sentry from "@sentry/react";
import { useQuery } from "react-query";

import axios from "axios";

export type ProjectLayerType =
  | "Well"
  | "Facility"
  | "Completion"
  | "Grid"
  | "Base Layer"
  | "shapefile"
  | "Shapefile Properties"
  // Referring to an organization shapefile
  | "Organization"
  // Referring to a System/McDaniel shapefile
  | "McDaniel"
  | "geomBin"
  | "Facility"
  | "Facility Label"
  // IPDB Geo Model layers
  | "Geo Model"
  | "Geo Model Controls"
  | "Rig Frac";

export interface IProjectLayer {
  Color: string | null;
  layerId: string;
  name: string;
  opacity?: number;
  order: number;
  projectLayerType: ProjectLayerType;
  properties?: string[];
  shapefileId: string;
  projectShapefileId?: string;
  StrokeColor: string | null;
  Thickness: number | null;
  LineStyle: string | null;
  FillPattern: string | null;
  //TODO BF: assign a type to this 'organization' | etc
  visibility: string;
  geomBinId?: string;
  // Rig/Frac color-by: the /values key, or null for a never-set layer. A legacy
  // layer may still hold the retired "none" sentinel; both read as the Current
  // Operator default (see resolveColorByField).
  colorByField?: Maybe<string>;
  // Rig/Frac color-by: value → hex overrides. Absent/omitted reads as {}.
  colorOverrides?: Record<string, string>;
}

export const PROJECT_LAYER_LIST_QUERY_KEYS = {
  currentProjectLayerList: "currentProjectLayerList"
};

export type UseProjectLayerListQueryKeysT = keyof typeof PROJECT_LAYER_LIST_QUERY_KEYS;

export type UseProjectLayerListQueryParamsT = {
  projectId: string;
  onSuccess?: (data) => void;
  onError?: (error) => void;
  select?;
  queryKey?: UseProjectLayerListQueryKeysT;
  isAutoRefetching?: boolean;
};

export type UseProjectLayerListQueryReturnT = {
  data: IProjectLayer[];
  error;
  isError: boolean;
  isIdle: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  refetch: () => void;
};

export const fetchProjectLayerList = async ({ queryKey }) => {
  const [, projectId] = queryKey;
  let response;
  try {
    response = await axios.get(
      `${process.env.REACT_APP_PROJECT_SERVICE}/project/projectLayers/${projectId}`
    );
  } catch (error) {
    throw new Error("Error fetching project layer list");
  }

  if (!Array.isArray(response.data)) {
    Sentry.withScope((scope) => {
      scope.setContext("fetchProjectLayerList", {
        projectId,
        status: response.status,
        contentType: response.headers?.["content-type"],
        dataType: response.data === null ? "null" : typeof response.data,
        responseURL: response.request?.responseURL
      });
      scope.setLevel("error");
      Sentry.captureMessage("Invalid project layer list response: expected an array");
    });
    throw new Error("Invalid project layer list response: expected an array");
  }

  return response;
};

export function useProjectLayerListQuery(
  params: UseProjectLayerListQueryParamsT
): UseProjectLayerListQueryReturnT {
  const {
    isAutoRefetching,
    onError,
    onSuccess,
    projectId,
    queryKey = "currentProjectLayerList",
    select
  } = params;

  const nextIsAutoFetching =
    isAutoRefetching !== undefined ? isAutoRefetching : !(params.projectId === undefined);

  const queryInfo = useQuery({
    queryKey: [queryKey, projectId],
    queryFn: fetchProjectLayerList,
    select,
    onError: (error) => {
      onError && onError(error);
    },
    onSuccess: (data) => {
      onSuccess && onSuccess(data);
    },
    enabled: nextIsAutoFetching
  });

  return {
    error: queryInfo.error,
    isError: queryInfo.status === "error",
    isIdle: queryInfo.status === "idle",
    isLoading: queryInfo.status === "loading",
    isSuccess: queryInfo.status === "success",
    refetch: queryInfo.refetch,
    data: queryInfo.data?.data
  };
}
